import React, { useMemo } from 'react';
import { getInitials, getAvatarColor } from '../utils/avatarUtils';
import '../styles/DefaultAvatar.css';

/**
 * DefaultAvatar - Google-style default avatar with initials
 * 
 * Displays a colored circle with user's initials when no profile image is available.
 * Color is determined by user identifier for consistency.
 * Uses SVG for perfect scaling and centering at any size.
 * 
 * @param {string} name - Username or display name for generating initials
 * @param {string|number} identifier - Unique identifier for consistent color (defaults to name)
 * @param {string} className - Additional CSS classes
 * @param {number|string} size - Size in pixels (default: auto, inherits from parent)
 * @param {object} style - Additional inline styles
 */
const DefaultAvatar = ({
  name = 'User',
  identifier = null,
  className = '',
  size = null,
  style = {},
  ...props
}) => {
  // Memoize calculations for performance
  const avatarData = useMemo(() => {
    const initials = getInitials(name);
    const color = getAvatarColor(identifier || name);
    return { initials, color };
  }, [name, identifier]);

  const computedStyle = useMemo(() => {
    const baseStyle = { ...style };
    
    if (size) {
      const sizeValue = typeof size === 'number' ? `${size}px` : size;
      baseStyle.width = sizeValue;
      baseStyle.height = sizeValue;
    }
    
    return baseStyle;
  }, [size, style]);

  return (
    <div 
      className={`default-avatar ${className}`}
      style={computedStyle}
      aria-label={`Avatar for ${name}`}
      {...props}
    >
      <svg 
        viewBox="0 0 100 100" 
        className="default-avatar__svg"
      >
        <circle 
          cx="50" 
          cy="50" 
          r="50" 
          fill={avatarData.color}
        />
        <text
          x="50"
          y="50"
          dy=".35em"
          textAnchor="middle"
          fill="white"
          fontSize="40"
          fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"
          fontWeight="500"
        >
          {avatarData.initials}
        </text>
      </svg>
    </div>
  );
};

export default DefaultAvatar;
